import Button from "../../../../../../components/base/button/button";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, CircularProgress, Divider, Fade, Paper, Skeleton, Tooltip, Typography} from "@mui/material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Grid from "@mui/material/Unstable_Grid2";
import classnames from "classnames";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import IconButton from "../../../../../../components/base/button/icon_button";
import {AddOutlined, DeleteOutlined, DragHandleRounded, EditOutlined, ListAltRounded, Remove} from "@mui/icons-material";
import Utils, {numComparator} from "../../../../../../../core/services/utils/utils";
import {ReactSortable} from "react-sortablejs";
import {useBizFormApiResolver} from "../../../../../../hooks/use-biz-form-api-resolver";


const NCRAStatusesSetting = () => {
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [data, _setData] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    /**
     * Sets the entries state of this component by sorting them based on their orderIndex after the new state is
     * received.
     *
     * This method acts as a wrapper that injects the sorting behaviour at the end of the setting this state.
     */
    const setData = useCallback((stateOrCallback, sort = false) => {
        _setData((prevState) => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState)
            } else {
                newState = stateOrCallback
            }
            if (sort) {
                const res = [...(newState ?? [])];
                res.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
                return res.map((e, index) => ({...e, orderIndex: index}));
            }
            return newState
        })
    }, [])

    /**
     * Reorders a lead status in the server, and if successful, sets the new state as well.
     */
    const reorderStatus = useCallback(async (e) => {
        const orderedEntries = Utils.reorderList(data, e.oldDraggableIndex, e.newDraggableIndex, 'orderIndex');
        setData(orderedEntries);
        setReordering(true);
        const response = await Api.changeNcrStatusIndex({
            id: data[e.oldDraggableIndex].id,
            newIndex: e.newDraggableIndex + 1
        })
        if (!isMounted())
            return;
        setReordering(false);
        if (!response?.resultFlag) {
            setData(data);
        }
    }, [data, isMounted, setData])

    /**
     * Fetches the entries in the system.
     */
    const getList = useCallback(async () => {
        setLoading(true);
        const response = await Api.listNcrStatuses();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data?.map(e => ({
                ...e,
                inEditMode: false,
                editing: false,
            })) ?? [], true);
        }
    }, [isMounted, setData]);

    /**
     * As soon as the dialog opens:
     * - fetches the list of entries
     */
    useEffect(() => {
        getList().then();
    }, [getList]);

    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(ValidateMessages.required)
            .trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Submits the form values to create a new entry
     */
    const createEntry = useCallback(async (values, form) => {
        setCreating(true);
        const response = await Api.createNcrStatus({
            title: values.title,
        });
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag && !response.aborted) {
            setData(prevState => [
                ...prevState,
                {
                    ...response.data,
                    inEditMode: false,
                    editing: false
                }
            ]);
            form.change('title', '')
            form.resetFieldState('title');
        }
    }, [isMounted, setData]);

    /**
     * Updates an entry with the given values.
     *
     * @param entry the entry to be updated.
     * @return {Promise<boolean>}
     */
    const updateEntry = useCallback(async (entry) => {
        setData(prevState => prevState.map(e =>
            e.id === entry.id
                ? {...e, editing: true}
                : e
        ));
        const response = await Api.updateNcrStatus({
            id: entry.id,
            title: entry.title,
            formReference: entry.formReference,
            updatedProperties: [
                'Title',
                "FormReference",
            ]
        })
        if (!isMounted())
            return false
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? ({
                ...e,
                title: entry.title,
                formReference: entry.formReference,
                inEditMode: false,
            }) : e) ?? []);
        }
        return response?.resultFlag ?? false
    }, [isMounted, setData])


    /**
     * Removes an entry
     * @param entry the entry to be removed
     */
    const removeEntry = useCallback(async (entry) => {
        const response = await Api.getNcrStatusRelatedReportNo(entry.id);
        if (!isMounted())
            return;

        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: data
                .filter(e => e.id !== entry.id)
                .map(e => ({
                    title: e.title,
                    id: e.id,
                    data: e,
                })),
            removedEntryTitle: `The Status: "${entry.title}". There are ${response?.data?.numberOfRelatedReports ?? 0} report(s) bound to this status. Shall we continue`,
            onClose: async (confirmed, data) => {
                if (!confirmed)
                    return;
                if (!data)
                    return;
                setData(prevState => prevState.map(e => e.id === entry.id
                    ? {...e, editing: true}
                    : e
                ));
                const response = await Api.deleteNcrStatus(entry.id, data.id)
                if (!isMounted())
                    return false
                if (response?.resultFlag) {
                    setData(prevState => prevState.filter(e => e.id !== entry.id), true);
                } else {
                    setData(prevState => prevState.map(e => e.id === entry.id ? ({...e, editing: false}) : e), true);
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [data, dispatch, isMounted, setData]);

    /**
     * Toggles the edit mode of the entry.
     */
    const toggleInEditMode = useCallback((entry, inEditMode = false) => {
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, inEditMode} : e))
    }, [setData])

    /**
     * Creates the styles for the container boxes.
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column'}}>
                <Form onSubmit={createEntry} validate={validate}>
                    <Box sx={boxMixin}>
                        <Typography variant={'subtitle2'} fontWeight={600}>
                            Create a new status
                        </Typography>
                        <Grid container xs={12} spacing={2} marginInline={0} marginBlock={2}>
                            <Grid xs>
                                <MuiRFF.TextField
                                    fullWidth
                                    size={'small'}
                                    name={"title"}
                                    label={"Title"}
                                    required={required.title}
                                />
                            </Grid>
                            <Grid xs={'auto'} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                <Button
                                    variant={'contained'}
                                    type={'submit'}
                                    color={'primary'}
                                    size={'large'}
                                    disabled={creating}
                                    sx={{height: 37}}
                                >
                                    {
                                        creating
                                            ? "Creating..."
                                            : "Create"
                                    }
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Form>
                {
                    !loading &&
                    <ReactSortable
                        list={data}
                        setList={() => void 0}
                        onUpdate={reorderStatus}
                        handle={'.handle'}
                        ghostClass={'ghost'}
                        animation={210}
                        swapThreshold={0.65}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 15,
                        }}
                    >
                        {
                            data?.map((entry, index, array) => (
                                <NCRAStatusesSettingCard
                                    key={entry.id}
                                    entry={entry}
                                    update={updateEntry}
                                    remove={removeEntry}
                                    removable={array.length > 1}
                                    setInEditMode={toggleInEditMode}
                                    sortable={!reordering && !array.some(e => e.editing || e.inEditMode)}
                                />
                            ))
                        }
                    </ReactSortable>
                }
                {
                    loading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    component={'div'}
                                    key={key}
                                    width={'100%'}
                                    height={146}
                                    sx={{transformOrigin: 'none', transform: 'none'}}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    !loading && data.length < 1 &&
                    <Typography variant={'caption'} color={'text.secondary'} textAlign={'center'} sx={{marginBlock: 6, width: '100%'}}>
                        No statuses found.
                    </Typography>
                }
            </Paper>
        </>
    )
}


export default NCRAStatusesSetting;

const NCRAStatusesSettingCard = ({
                                     update,
                                     remove,
                                     setInEditMode,
                                     ...props
                                 }) => {
    const ncrBizForm = useBizFormApiResolver((t) => t.ncrService);
    const [creatingForm, setCreatingForm] = useState(false);
    const [removingForm, setRemovingForm] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(ValidateMessages.required)
            .trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        title: props.entry.title,
    }), [props.entry.title])

    /**
     * Updates the entry by calling the appropriate callback in the props.
     */
    const updateEntry = useCallback(async (values) => {
        if (
            props.entry.title === values.title
        ) {
            return setInEditMode(props.entry, false);
        }
        await update({...props.entry, title: values.title});
    }, [props.entry, update, setInEditMode])

    /**
     * Removes the entry by calling the appropriate callback in the props.
     */
    const removeEntry = useCallback(async () => {
        await remove(props.entry)
    }, [remove, props.entry])

    /**
     * Set the entry as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        setInEditMode(props.entry, true);
    }, [setInEditMode, props.entry])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        setInEditMode(props.entry, false);
        form.reset(initialValues);
    }, [initialValues, props.entry, setInEditMode]);

    /**
     * Creates a form for the status
     * @type {(function(): Promise<void>)}
     */
    const createForm = useCallback(async () => {
        setCreatingForm(true);
        let title = `${props.entry.title}'s Questionnaire`;
        if (title.length > 50) {
            title = "Form Questionnaire";
        }
        const response = await ncrBizForm.createForm({
            title: title,
            description: `This form is created for the ${props.entry.title} status of the NCR.`,
            isPublic: false,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await update({...props.entry, formReference: response.data.id});
        }
        if (!isMounted())
            return;
        setCreatingForm(false);
    }, [isMounted, props.entry, ncrBizForm, update])

    /**
     * Opens the edit form dialog
     * @type {(function(): void)}
     */
    const editForm = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.editBizForm, {
            open: true,
            formReference: props.entry.formReference,
            formApiResolver: (t) => t.ncrService,
        }))
    }, [props.entry.formReference, dispatch])

    /**
     * Removes the form from the status
     * @type {(function(): Promise<void>)|*}
     */
    const removeForm = useCallback(async () => {
        setRemovingForm(true);
        const response = (await ncrBizForm.deleteForm({id: props.entry.formReference,}));
        if (response?.resultFlag) {
            await update({...props.entry, formReference: null});
        }
        if (!isMounted())
            return;
        setRemovingForm(false);
    }, [isMounted, ncrBizForm, props.entry, update])

    return (
        <>
            <Card variant={'outlined'}>
                <Form
                    onSubmit={updateEntry}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form}) => (
                        <Grid container spacing={2} marginInline={0} marginBlock={0}>
                            <Grid xs={'auto'} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                <Fade in={props.sortable}>
                                    <IconButton color={'paper.primary'} className={'handle'}>
                                        <DragHandleRounded/>
                                    </IconButton>
                                </Fade>
                            </Grid>
                            <Grid xs paddingInline={0}>
                                <MuiRFF.TextField
                                    fullWidth
                                    size={'small'}
                                    name={"title"}
                                    label={"Title"}
                                    required={required.title}
                                    className={classnames({'show-as-text': !props.entry.inEditMode})}
                                />
                            </Grid>
                            <Grid xs={'auto'}>
                                {
                                    !props.entry.inEditMode &&
                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                        {
                                            !!props.entry?.formReference &&
                                            <Tooltip title={'View and Edit Form'}>
                                                <IconButton
                                                    disabled={removingForm}
                                                    onPressed={editForm}
                                                    color={'paper.secondary'}
                                                >
                                                    <ListAltRounded/>
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {
                                            !!props.entry?.formReference &&
                                            <Tooltip title={'Delete Form'}>
                                                <IconButton
                                                    disabled={removingForm}
                                                    onPressed={removeForm}
                                                    color={'paper.error'}
                                                >
                                                    {
                                                        creatingForm
                                                            ? <CircularProgress size={15} color={'error'}/>
                                                            : <Remove/>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {
                                            !props.entry?.formReference &&
                                            <Tooltip title={'Create Form'}>
                                                <IconButton
                                                    disabled={creatingForm}
                                                    onPressed={createForm}
                                                    color={'paper.secondary'}
                                                >
                                                    {
                                                        creatingForm
                                                            ? <CircularProgress size={15} color={'error'}/>
                                                            : <AddOutlined/>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        <Divider flexItem orientation={'vertical'}/>
                                        <Fade in={props.removable}>
                                            <IconButton
                                                disabled={!props.removable}
                                                onPressed={removeEntry}
                                                color={'paper.error'}
                                            >
                                                <DeleteOutlined/>
                                            </IconButton>
                                        </Fade>
                                        <IconButton onPressed={setAsEditing} color={'paper.primary'}>
                                            <EditOutlined/>
                                        </IconButton>
                                    </Box>
                                }
                                {
                                    props.entry.inEditMode &&
                                    <SaveOrRollback
                                        show
                                        column={false}
                                        showText={false}
                                        onSave={() => form.submit()}
                                        onRollback={() => rollbackValues(form)}
                                        loading={props.entry.editing}
                                    />
                                }
                            </Grid>
                        </Grid>
                    )}
                </Form>
            </Card>
        </>
    );
}
